import React, { useState, useEffect, useReducer, useMemo, useCallback, useRef } from 'react'
import FullCalendar from '@fullcalendar/react'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'
import { useTheme } from '@emotion/react'
import { Chip, Fade, LinearProgress, Stack } from '@mui/material'
import PlannerCalendarToolbar from './PlannerCalendarToolbar'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import './plannerStyle.css'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import CalendarEventItem from './CalendarEventItem'
import JobEventItem from './JobEventItem'
import moment from 'moment'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import useDateFormatter from '../generic/hooks/useDateFormatter/useDateFormatter'
import CalendarSidePanel, { CalendarSidePanelActions } from './CalendarSidePanel'
import { useToggle } from '../generic/hooks/useToggle'
import NotPlannedResourcesAlert from './NotPlannedResourcesAlert'
import CalendarWaitItem from './CalendarWaitItem'
import MachineRowLabel from './MachineRowLabel'
import useDeliveries from './useDeliveries'
import {
  HAS_SELECTED_FO_CLASS, LATE_FO_BORDER_COLOR, LATE_FO_CLASS, SELECTED_FO_BORDER_COLOR, SELECTED_FO_CLASS, fabricationOrderMarksOf, lateFabricationOrderIdsOf,
  useSelectedFabricationOrderPresence
} from './ganttFabricationOrderMarks'
import GanttLegend from './GanttLegend'

const CALENDAR_ITEM_HEIGHT = '3rem'
// The neutral border of every job bar (story 175, DL-2 a: the red one is no longer per task).
const JOB_BORDER_COLOR = 'black'
// Story 175 (contract §7): stable fallback, so a Planner that does not wire the machine entry does not
// hand FullCalendar a new column definition on every render.
const NO_ELIGIBLE_DEVICES = []
// Thin progress bar overlaid on the bottom edge of the sticky header while data loads
const LOADING_INDICATOR_HEIGHT = '3px'

// Import typedefs from another file
/**
 * @typedef {import('../generic/utilities/objectStructures/planner/eventInfo').EventInfo} EventInfo EventInfo object
 * @typedef {import('../generic/utilities/objectStructures/planner/planification').Planification} Planification Planification object
 * @typedef {import('../generic/utilities/objectStructures/planner/eventInfo').ExtendedProps} ExtendedProps ExtendedProps object
 */

/**
 *
 * @param {Object} props Props object
 * @param {function()} props.onEventClick Method triggered after clicking on a job
 * @param {Number} props.planificationId Planification id
 * @param {String} props.unitCriteriaName Planification selected criteria name
 * @param {React.Dispatch<React.SetStateAction<Planification>} props.setSelectedFile selectedFile use state setter
 * @param {boolean} props.isPlannerWorking If true, planner is running
 * @param {function()} props.showAlert Function to show alert
 */
const Calendar = (props) => {
  const {
    onEventClick,
    planificationId,
    selectedFile,
    setSelectedFile,
    isPlannerWorking,
    forceReloadCalendar,
    unitCriteriaName,
    eventChanges,
    resourceFamily,
    calendarRef,
    isFamilyViewActive,
    isThisEventFamily,
    showAlert,
    plannerFilters,
    eligibleDeviceIds = NO_ELIGIBLE_DEVICES,
    onMachineClick,
    metricsVersion = 0,
    selectedFabricationOrderId = null,
    onSelectedFabricationOrderGone
  } = props
  const [currentSetDatesInfo, setCurrentSetDatesInfo] = useState(null)
  const { t_, i18n } = useSmartTranslation()
  const theme = useTheme()
  const { formatDate, formatTime } = useDateFormatter()
  const [forceReloadNotPlannedItems, reloadNotPlannedItems] = useToggle()
  const [plannerDevices, setPlannerDevices] = useState([])
  const [resourceUpdateFlag, setResourceUpdateFlag] = useState(false)
  const isUserPlannerAuthorized = usePlannerAuthorization()
  // Story 175, CU12 (RN44, RN52, contract §2): the delivery of every fabrication order, asked with the SAME
  // global filters as the Gantt and only while the Gantt is on screen -- this component is unmounted when
  // the Timeline block is folded. `metricsVersion` re-reads it after every write of the plan (RN51).
  const deliveries = useDeliveries({
    planificationId,
    plannerFilters,
    metricsVersion,
    enabled: isUserPlannerAuthorized('PLANNER')
  })
  const lateFabricationOrderIds = useMemo(() => lateFabricationOrderIdsOf(deliveries.data), [deliveries.data])
  useSelectedFabricationOrderPresence({
    deliveries: deliveries.data,
    selectedFabricationOrderId,
    onGone: onSelectedFabricationOrderGone
  })
  const hasSelectedFabricationOrder = selectedFabricationOrderId !== null && selectedFabricationOrderId !== undefined
  // The code of the selected order for the legend pill, read from the same `/deliveries` answer the borders use;
  // an order Sun did not classify (RN27) is still selected, the pill then just says "Selected FO".
  const selectedFabricationOrderCode = useMemo(() => {
    if (!hasSelectedFabricationOrder || !deliveries.data || !Array.isArray(deliveries.data.fabricationOrders)) {
      return ''
    }
    const selected = deliveries.data.fabricationOrders
      .find((fabricationOrder) => fabricationOrder.fabricationOrderId === Number(selectedFabricationOrderId))
    return selected ? selected.fabricationOrderCode : ''
  }, [hasSelectedFabricationOrder, deliveries.data, selectedFabricationOrderId])
  // With `eventMaxStack={1}` overlapping jobs collapse into one visible bar plus a "+N" link, and FullCalendar
  // does not expose which events hide behind that link. So the selected order is brought to the FRONT of every
  // stack instead: its jobs are placed first and become the visible bar, with their selection ring, and the rest
  // go behind the "+N". Without a selection this is FullCalendar's own default order.
  const eventOrder = useMemo(() => {
    const selectedRank = (event) => (hasSelectedFabricationOrder && event.jobEvent &&
      Number(event.fabricationOrderId) === Number(selectedFabricationOrderId)
      ? 0
      : 1)
    return [(a, b) => selectedRank(a) - selectedRank(b), 'start', '-duration', 'allDay', 'title']
  }, [hasSelectedFabricationOrder, selectedFabricationOrderId])
  const eventClassNames = useCallback(
    (eventInfo) => fabricationOrderMarksOf(eventInfo.event, lateFabricationOrderIds, selectedFabricationOrderId),
    [lateFabricationOrderIds, selectedFabricationOrderId]
  )
  const getInitialGroupby = () => {
    const permissionsMap = [
      ['ASSETS_VIEW', 'devices'],
      ['PARTS_VIEW', 'parts'],
      ['TECHNOLOGIES_VIEW', 'technologies'],
      ['REFERENCES_VIEW', 'references'],
      ['FABRICATION_ORDERS_VIEW', 'fabrication_orders'],
      ['WORKERS_VIEW', 'workers']
    ]

    for (const [permission, value] of permissionsMap) {
      if (isUserPlannerAuthorized(permission)) return value
    }
    return null
  }
  const [groupby, setGroupby] = useState(getInitialGroupby)
  // The entry whose detail is open beside the calendar. Null means no panel, and then the calendar
  // takes the full width -- the panel pushes, it never floats over the rows.
  const [panelEvent, setPanelEvent] = useState(null)
  // True while events or resources are being fetched
  const [isFetchingCalendarData, setIsFetchingCalendarData] = useState(false)
  const pendingFetchCountRef = useRef(0)
  const flushFetchingStateRef = useRef(null)

  // A fetch can start while React is rendering (changing the view calls
  // calendar.changeView() from the duration reducer), and state cannot be set then, so
  // the flag is always applied in a later task. Coalescing also avoids flickering when
  // events and resources are fetched back to back.
  const flushFetchingState = useCallback(() => {
    clearTimeout(flushFetchingStateRef.current)
    flushFetchingStateRef.current = setTimeout(() => setIsFetchingCalendarData(pendingFetchCountRef.current > 0), 0)
  }, [])

  useEffect(() => () => clearTimeout(flushFetchingStateRef.current), [])

  /**
   * Runs a calendar data fetch keeping the loading indicator in sync
   * @param {function(): Promise} fetchData Fetch to track
   */
  const trackFetch = useCallback(async (fetchData) => {
    pendingFetchCountRef.current++
    flushFetchingState()
    try {
      return await fetchData()
    } finally {
      pendingFetchCountRef.current--
      flushFetchingState()
    }
  }, [flushFetchingState])

  useEffect(() => {
    if (!eventChanges || !calendarRef || !calendarRef.current) {
      return
    }
    const calendarEvent = calendarRef.current.calendar.getEvents().find(e => parseInt(e.id) === eventChanges.id)
    if (!calendarEvent) {
      return
    }

    const newDevice = calendarRef.current.calendar.getResources().find(e => e.title === eventChanges.machineName)

    calendarEvent.setExtendedProp('machineName', eventChanges.machineName)
    calendarEvent.setExtendedProp('deviceId', parseInt(newDevice.id))
    // Story 96 (RN27/INV6): only the date pin survives; the machine pin is no longer carried on the
    // calendar event, which is what makes the pin icon answer to the date alone (RN28).
    calendarEvent.setExtendedProp('pinnedDate', eventChanges.pinnedDate)
    calendarEvent.setResources([newDevice])
    calendarEvent.setStart(eventChanges.dateStart)
    calendarEvent.setEnd(eventChanges.dateEnd)
  }, [eventChanges, calendarRef])
  const changeView = (action) => {
    let view = 'resourceTimeLine'
    switch (action?.mode) {
      case 'days':
        view = 'resourceTimeLine'
        break
      case 'weeks':
        view = 'weekTimeLine'
        break
      case 'months':
        view = 'monthTimeLine'
        break
      case 'years':
        view = 'yearTimeLine'
        break
      default:
        break
    }
    calendarRef.current.calendar.changeView(view)
    return { type: action.type, mode: action.mode }
  }

  const calculateZoom = (state, action) => {
    const { mode } = state
    let newMode
    let isMaxValue = false
    let isMinValue = false

    if (action.type === 'ZOOM_IN') {
      switch (mode) {
        case 'years':
          newMode = 'months'
          break
        case 'months':
          newMode = 'weeks'
          break
        case 'weeks':
          newMode = 'days'
          isMinValue = true
          break
        default:
          newMode = 'days'
          isMinValue = true
          break
      }
    } else if (action.type === 'ZOOM_OUT') {
      switch (mode) {
        case 'days':
          newMode = 'weeks'
          break
        case 'weeks':
          newMode = 'months'
          break
        case 'months':
          newMode = 'years'
          isMaxValue = true
          break
        default:
          newMode = 'years'
          isMaxValue = true
          break
      }
    }
    changeView({ mode: newMode, type: action.type })
    return { mode: newMode, isMaxValue, isMinValue }
  }

  const onDispatchDurationChange = (state, action) => {
    if (state === null) {
      return { type: action.type, mode: action.mode }
    }

    switch (action.type) {
      case 'SET_VIEW':
        return changeView(action)
      case 'ZOOM_IN':
        return calculateZoom(state, action)
      case 'ZOOM_OUT':
        return calculateZoom(state, action)
      default:
        break
    }
  }

  const [currentDuration, dispatchNewDuration] = useReducer(onDispatchDurationChange, { type: 'SET_VIEW', mode: 'days' }, (arg) => onDispatchDurationChange(null, arg))

  useEffect(() => {
    if (!calendarRef.current) {
      return
    }
    calendarRef.current.calendar.refetchEvents()
    calendarRef.current.calendar.refetchResources()
  }, [forceReloadCalendar, plannerFilters, calendarRef])

  const locale = useMemo(() => {
    if (i18n.language.startsWith('es')) {
      return 'es'
    } else if (i18n.language.startsWith('fr')) {
      return 'fr'
    } else if (i18n.language.startsWith('en')) {
      return 'en-gb'
    } else if (i18n.language.startsWith('pt')) {
      return 'pt'
    } else if (i18n.language.startsWith('ca')) {
      return 'ca'
    } else if (i18n.language.startsWith('de')) {
      return 'de'
    }
    return 'en-gb'
  }, [i18n.language])

  useEffect(() => {
    document.documentElement.style.setProperty('--fc-border-color', theme.palette.divider)
    document.documentElement.style.setProperty('--fc-page-bg-color', theme.palette.background.default)
    document.documentElement.style.setProperty('--fc-list-event-hover-bg-color', theme.palette.background.default)
    document.documentElement.style.setProperty('--fc-button-border-color', theme.palette.Button)
    document.documentElement.style.setProperty('--fc-button-bg-color', theme.palette.Button)
    document.documentElement.style.setProperty('--fc-button-hover-bg-color', theme.palette.Button)
    document.documentElement.style.setProperty('--planner-timeline-background-color', theme.palette.background.color_373636)
    document.documentElement.style.setProperty('--planner-timeline-background-color-header', theme.palette.background.row2)
    document.documentElement.style.setProperty('--planner-timeline-background-color-odd', theme.palette.row2)
    document.documentElement.style.setProperty('--planner-timeline-border-color', theme.palette.primary.main + '80')
    document.documentElement.style.setProperty('--fc-bg-event-opacity', 1)
  }, [theme])

  const checkFirsWaitResource = useCallback((resources) => {
    let firstWaitDone = false
    return resources.map(item => {
      let firstWait = false
      if (item.wait && !firstWaitDone) {
        firstWaitDone = true
        firstWait = true
      }
      return {
        ...item,
        firstWait
      }
    })
  }, [])

  const getResources = useCallback(async () => {
    if (resourceUpdateFlag === null) {
      return
    }
    if (!planificationId) {
      return []
    }
    if (groupby === 'technology') {
      const resources = await apiGet('delayedPlannedResourceModification/' + groupby + '/' + planificationId)
      return checkFirsWaitResource(resources)
    }
    if (groupby === 'devices') {
      const visibleDevices = plannerDevices.filter(item => item.visible)
      if (visibleDevices.length === 0) return []
      let resources = await apiPost('delayedPlannedResourceModification/' + groupby + '/' + planificationId, visibleDevices)
      if (resources.length === 0) return []
      const orderIndexMap = new Map(visibleDevices.map(device => [device.name, device.orderIndex]))

      resources = resources.map(item => {
        return {
          ...item,
          orderIndex: orderIndexMap.get(item.title) ?? null
        }
      })

      return checkFirsWaitResource(resources)
    }
    return await apiGet('delayedPlannedResourceModification/' + groupby + '/' + planificationId)
  }, [groupby, planificationId, plannerDevices, resourceUpdateFlag, checkFirsWaitResource])

  // Story 175, DL-2 (a) (RN44, S140/S141): the per-TASK `late` flag (end after the order's delivery date) is
  // still computed here, because it is what the drop request reports to Sun (`oldLate`/`newLate`) and what
  // Sun's "Delayed/Planned" row column counts -- that column does not change in this story. It no longer
  // decides the BORDER: every job keeps the neutral one, and the red one belongs to the fabrication orders
  // Sun classifies as `LATE` (`eventClassNames`, from `/deliveries`).
  const updateLate = useCallback((event, isFullCalendarEvent, fullCalendarEnd) => {
    if (!isFullCalendarEvent && !event.jobEvent) {
      event.borderColor = 'transparent'
      event.color = 'transparent'
      return
    }

    const deliveryDateFO = moment(isFullCalendarEvent ? event.extendedProps.deliveryDateFO : event.deliveryDateFO)
    const end = moment(isFullCalendarEvent ? fullCalendarEnd : event.end)
    const late = (end > deliveryDateFO)
    if (isFullCalendarEvent) {
      // Check if we need to update resources counter
      calendarRef.current.calendar.refetchResources()

      event.setExtendedProp('late', late)
    } else {
      event.late = late
      event.borderColor = JOB_BORDER_COLOR
    }
  }, [calendarRef])

  /**
   * Method used to update job
   * @param {EventInfo} eventDropInfo
   */
  const updateJob = useCallback(async (eventDropInfo, newEnd) => {
    const eventId = eventDropInfo.event.id
    const newResourceId = eventDropInfo.newResource != null ? eventDropInfo.newResource.id : null
    const oldResourceId = eventDropInfo?.oldResource?._resource?.id || eventDropInfo?.event?._def?.resourceIds[0]
    const newStart = eventDropInfo.event.start
    const partId = eventDropInfo.event.extendedProps.partId
    updateLate(eventDropInfo.event, true, newEnd)
    const oldLate = eventDropInfo?.oldEvent.extendedProps.late
    const newLate = eventDropInfo.event.extendedProps.late
    await apiPut('planificationJobModification/update/' + planificationId, { id: eventId, resourceId: newResourceId, oldResourceId, dateStart: newStart.valueOf(), dateEnd: newEnd.valueOf(), partId, fabricationOrderId: eventDropInfo.event.extendedProps.fabricationOrderId, taskId: eventDropInfo.event.extendedProps.taskReferenceId, oldLate, newLate, jobReferenceId: eventDropInfo.event.extendedProps.jobReferenceId })
    setSelectedFile((oldState) => {
      if (oldState.editing) {
        return oldState
      }
      return { ...oldState, editing: true }
    })
    setResourceUpdateFlag(prev => !prev)
  }, [setSelectedFile, updateLate, planificationId])

  const checkMovedEventIsFamily = useCallback(async (currentJob, event) => {
    try {
      const isJobMode = unitCriteriaName === 'Job'
      const response = await apiGet('planner/checkFamilyMove/' + planificationId + '/' + currentJob.jobReferenceId + '/' + event.end + '/' + event.start + '/' +
        (isJobMode ? currentJob.partId : null) + '/' + currentJob.fabricationOrderId)
      if (response === false) {
        return showAlert({
          severity: 'warning',
          title: t_('This operation is not allowed'),
          message: t_('Check resources family') + ': ' + (isJobMode ? currentJob.partName : currentJob.nameFO)
        })
      }
      return response
    } catch (error) {
      return false
    }
  }, [planificationId, unitCriteriaName, showAlert, t_])

  /**
   * Method used to drop event
   * @param {EventInfo} eventDropInfo
   */
  const eventDrop = useCallback((eventDropInfo) => {
    const machineNames = eventDropInfo.event.extendedProps.machineNames
    const originalDuration = eventDropInfo?.oldEvent?.end?.valueOf() - eventDropInfo?.oldEvent?.start?.valueOf()
    // Control forecast devices availability
    const forecastEvent = calendarRef.current.calendar.getEvents().find((event) => {
      return event.extendedProps.forecast === true
    }
    )

    const eventResource = eventDropInfo.newResource ? eventDropInfo.newResource : eventDropInfo.oldEvent.getResources()[0]

    if (forecastEvent && eventDropInfo.event.start.valueOf() < forecastEvent.end.valueOf()) {
      const resource = forecastEvent.getResources()[0]
      if (resource.id !== eventResource.id) {
        return false
      }
      eventDropInfo.revert()
      return
    }

    const currentJob = calendarRef.current.calendar.getEvents().find(job =>
      job.extendedProps.partId === eventDropInfo.event.extendedProps.partId &&
        job.extendedProps.currentJobNum === eventDropInfo.event.extendedProps.currentJobNum
    )

    let previousJob = calendarRef.current.calendar.getEvents().find(job =>
      job.extendedProps.partId === eventDropInfo.event.extendedProps.partId &&
        job.extendedProps.currentJobNum === eventDropInfo.event.extendedProps.currentJobNum - 1
    )

    let nextJob = calendarRef.current.calendar.getEvents().find(job =>
      job.extendedProps.partId === eventDropInfo.event.extendedProps.partId &&
        job.extendedProps.currentJobNum === eventDropInfo.event.extendedProps.currentJobNum + 1
    )

    let end = eventDropInfo.event.end
    if (!end) {
      end = new Date(eventDropInfo.event.start.valueOf() + originalDuration)
      eventDropInfo.event.setEnd(end)
    }

    if (unitCriteriaName === 'Fabrication Order') {
      previousJob = calendarRef.current.calendar.getEvents().find(job =>
        job.extendedProps.fabricationOrderId === eventDropInfo.event.extendedProps.fabricationOrderId &&
            job.extendedProps.currentJobNum === eventDropInfo.event.extendedProps.currentJobNum - 1
      )
      nextJob = calendarRef.current.calendar.getEvents().find(job =>
        job.extendedProps.fabricationOrderId === eventDropInfo.event.extendedProps.fabricationOrderId &&
            job.extendedProps.currentJobNum === eventDropInfo.event.extendedProps.currentJobNum + 1
      )
    }

    if (previousJob !== undefined && eventDropInfo.event.start.valueOf() < previousJob.end.valueOf()) {
      const newStart = previousJob.end
      end = new Date(newStart.valueOf() + originalDuration)
      eventDropInfo.event.setStart(newStart)
      eventDropInfo.event.setEnd(end)
    }

    if (nextJob !== undefined && end.valueOf() > nextJob.start.valueOf()) {
      end = nextJob.start
      const newStart = new Date(end.valueOf() - originalDuration)
      eventDropInfo.event.setStart(newStart)
      eventDropInfo.event.setEnd(end)
    }
    // Get the unavailable calendar events of the device is dropped
    const resourceEventDropId = eventDropInfo.event.getResources()[0].id
    const unavailableCalendarEvents = calendarRef.current.calendar.getEvents().filter(event => {
      return !event.extendedProps.jobEvent && event.getResources()[0].id === resourceEventDropId
    })
    if (colidingWithNoWorkingEvent(unavailableCalendarEvents, eventDropInfo.event)) {
      eventDropInfo.revert()
      return
    }
    checkMovedEventIsFamily(currentJob.extendedProps, eventDropInfo.event)
      .then(canMove => {
        if (!canMove) {
          eventDropInfo.revert()
          return
        }
        if (eventDropInfo.newResource != null) {
          if (machineNames.includes(eventDropInfo.newResource.title)) {
            updateJob(eventDropInfo, end)
          } else {
            eventDropInfo.revert()
          }
        } else {
          updateJob(eventDropInfo, end)
        }
      })
  }, [updateJob, unitCriteriaName, calendarRef, checkMovedEventIsFamily])
  const colidingWithNoWorkingEvent = (unavailableEvents, droppedEvent) => {
    const droppedStart = droppedEvent.start.valueOf()
    const droppedEnd = droppedEvent.end.valueOf()
    return unavailableEvents.some(unavailableEvent => {
      const unavailableStart = unavailableEvent.start.valueOf()
      const unavailableEnd = unavailableEvent.end.valueOf()
      // Check if new event drop is between a conflicting event
      const startsInside = droppedStart >= unavailableStart && droppedStart <= unavailableEnd
      // Check if new event drop is at the start of a conflicting event
      const endsInside = droppedEnd >= unavailableStart && droppedEnd <= unavailableEnd
      // Check if a conflicting event is inside of the new event drop
      const wrapsEvent = unavailableStart >= droppedStart && unavailableStart <= droppedEnd
      return startsInside || endsInside || wrapsEvent
    })
  }

  useEffect(() => {
    const getDeviceList = async () => {
      return await apiGet('devicesPlannerSummary')
    }
    // Tracked as well: events and resources resolve to an empty calendar until the
    // device list is available, so this is part of what the user is waiting for.
    trackFetch(getDeviceList).then(deviceList => {
      deviceList.sort((a, b) => a.orderIndex - b.orderIndex)
      setPlannerDevices(deviceList)
    })
  }, [groupby, planificationId, trackFetch])

  const loadPlanification = useCallback(async (startTime, endTime, groupby) => {
    if (!planificationId) {
      return []
    }
    if (groupby === null) {
      return []
    }

    const visibleDevicesIds = plannerDevices
      .filter(item => item.visible)
      .map(item => item.deviceId)
    if (visibleDevicesIds.length === 0) return []
    const response = await apiPost('planner/planificationWithCalendarEvents', { planificationId, startDate: startTime, endDate: endTime, groupBy: groupby, devicesIds: visibleDevicesIds, plannerFilters })
    response.forEach(element => {
      if (!element.jobEvent && !element.wait) {
        element.display = 'background'
        element.color = 'transparent'
      }
    })
    reloadNotPlannedItems()
    setResourceUpdateFlag(prev => !prev)
    return response
  }, [planificationId, plannerDevices, reloadNotPlannedItems, plannerFilters])

  const onDateChange = (event) => {
    if (event === 'prev') {
      calendarRef.current.calendar.prev()
    } else if (event === 'next') {
      calendarRef.current.calendar.next()
    } else if (event === 'today') {
      calendarRef.current.calendar.today()
    }
  }

  const headerText = useMemo(() => {
    if (resourceUpdateFlag === null) {
      return
    }
    switch (groupby) {
      case 'devices':
        return t_('Assets')
      case 'parts':
        return t_('Parts')
      case 'technologies':
        return t_('Technologies')
      case 'references':
        return t_('References')
      case 'fabrication_orders':
        return t_('Fabrication orders')
      case 'workers':
        return t_('Workers')
      default:
        return null
    }
  }, [groupby, t_, resourceUpdateFlag])

  const onSlotLaneDidMount = useCallback((info) => {
    if (info.resource.extendedProps.firstWait) {
      info.el.style.borderTop = '5px dashed white'
    }
  }, [])

  // Story 175, CU5 (S84/S85, RN4/RN5, contract §7): the row label opens the machine detail if and only
  // if the Gantt is grouped by machine, the row is not the wait row and Sun listed the machine as
  // eligible. Every other label keeps FullCalendar's default content (`true`): no link look, no click.
  const renderTitleCell = useCallback((cellInfo) => {
    const resourceId = cellInfo.resource.id
    // The wait lane's title is Sun's frozen literal "Wait" (ResourceMapper, H146 S73): Jupiter translates it here.
    if (resourceId === 'wait') {
      return t_('Wait')
    }
    const isEligibleMachineRow = groupby === 'devices' && resourceId !== 'wait' && eligibleDeviceIds.includes(Number(resourceId))
    if (!onMachineClick || !isEligibleMachineRow) {
      return true
    }
    return <MachineRowLabel label={cellInfo.fieldValue} onClick={() => onMachineClick(Number(resourceId))} />
  }, [groupby, eligibleDeviceIds, onMachineClick, t_])

  const resourceAreaColumns = useMemo(() => {
    if (resourceUpdateFlag === null) {
      return []
    }

    return [
      {
        field: 'title',
        headerContent: headerText,
        width: '60%',
        cellDidMount: onSlotLaneDidMount,
        cellContent: renderTitleCell
      },
      {
        field: 'lateJobsTotalJobs',
        headerContent: t_('Delayed/Planned'),
        width: '40%',
        cellDidMount: onSlotLaneDidMount

      }

    ]
  }, [headerText, t_, resourceUpdateFlag, onSlotLaneDidMount, renderTitleCell])

  const views = useMemo(() => {
    return {
      resourceTimeLine: {
        type: 'resourceTimeline',
        duration: { days: 2 },
        slotLabelFormat: [{ month: 'short', day: 'numeric' }, { hour: 'numeric', minute: '2-digit' }],
        slotDuration: { minutes: 30 },
        slotLabelInterval: { hour: 1 }
      },
      weekTimeLine: {
        type: 'resourceTimeline',
        duration: { weeks: 2 },
        slotLabelFormat: [{ week: 'long' }, { month: 'short', day: 'numeric' }],
        slotDuration: { hour: 12 },
        slotLabelInterval: { hour: 24 }
      },
      monthTimeLine: {
        type: 'resourceTimeline',
        duration: { months: 2 },
        slotLabelFormat: [{ month: 'long' }, { month: 'short', day: 'numeric' }],
        slotDuration: { hour: 24 },
        slotLabelInterval: { hour: 24 * 7 }
      },
      yearTimeLine: {
        type: 'resourceTimeline',
        duration: { years: 2 },
        slotLabelFormat: [{ year: 'numeric' }, { month: 'short' }],
        slotDuration: { month: 1 },
        slotLabelInterval: { month: 1 }
      }
    }
  }, [])

  // A click SELECTS the entry and opens its detail beside the calendar. It no longer jumps straight
  // into the settings dialog: reading the detail used to require hovering, which is unusable on a
  // shop-floor touch screen and impossible with gloves, so the reading surface now comes first and
  // the dialog is one deliberate press away, from the foot of the panel.
  //
  // Unlike the dialog, the panel opens for EVERY kind of entry -- a wait and an unavailable device
  // have a detail too, and clicking them used to do nothing at all.
  const onEventClickCallback = useCallback((e) => {
    setPanelEvent(e.event)
  }, [])

  // The foot actions of the panel: these do open the settings dialog, on the tab that matches the
  // action pressed. Gated exactly as before -- `onEventClick` is a no-op unless the plan is editable
  // -- so this adds no reach that the click did not already have.
  const openEventDialog = useCallback((tabKey) => {
    if (!panelEvent?.extendedProps?.jobEvent) {
      return
    }
    onEventClick({ ...panelEvent.extendedProps, initialTabKey: tabKey })
  }, [onEventClick, panelEvent])

  // Close the panel when planning starts: the calendar clears every event while planning, and a
  // panel left open would describe a job that is no longer on the grid. Inherited from the tooltip
  // this panel replaces, where the same staleness was the reason for the rule.
  useEffect(() => {
    if (isPlannerWorking) {
      setPanelEvent(null)
    }
  }, [isPlannerWorking])

  // The toolbar bar wraps onto a second line on narrow screens; FullCalendar's sticky date header sits right under
  // it, so it follows the bar's measured height instead of a fixed offset.
  const toolbarBarRef = useRef(null)
  const [toolbarBarHeight, setToolbarBarHeight] = useState(0)
  useEffect(() => {
    const bar = toolbarBarRef.current
    if (!bar) return () => {}
    const measure = () => setToolbarBarHeight(bar.offsetHeight)
    measure()
    if (typeof window.ResizeObserver !== 'function') return () => {}
    const observer = new window.ResizeObserver(measure)
    observer.observe(bar)
    return () => observer.disconnect()
  }, [])

  return (
    <Stack gap={1}>
      <NotPlannedResourcesAlert planificationId={planificationId} unitCriteriaName={unitCriteriaName} reload={forceReloadNotPlannedItems} />
      <div ref={toolbarBarRef} data-testid='gantt-toolbar-bar' style={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: theme.palette.background.default }}>
        {/* Story 175, RN47 (S147): the legend of the two border marks sits on the toolbar's own row. Story 177: the
            row wraps when the screen is narrow instead of making the whole page wider than it, and FullCalendar's
            sticky header follows the bar's real height. */}
        <Stack direction='row' alignItems='center' columnGap={2} rowGap={1} flexWrap='wrap' style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <PlannerCalendarToolbar
              selectedFile={selectedFile}
              onDateChange={onDateChange}
              setDatesInfo={currentSetDatesInfo}
              currentDuration={currentDuration}
              dispatchNewDuration={dispatchNewDuration}
              setGroupby={setGroupby}
              groupby={groupby}
              plannerDevices={plannerDevices}
              setPlannerDevices={setPlannerDevices}
              showAlert={showAlert}
              unitCriteriaName={unitCriteriaName}
            />
          </div>
          {/* The ✕ of the pill clears the selection: the same Planner callback that already clears it when the
              order leaves the plan (`clearSelectedFabricationOrder`), so no new wiring crosses Timeline. */}
          <GanttLegend
            hasSelection={hasSelectedFabricationOrder}
            selectedFabricationOrderCode={selectedFabricationOrderCode}
            onClearSelection={onSelectedFabricationOrderGone}
          />
        </Stack>
        {/* Absolutely positioned so it takes no space in the flow: the calendar never
            shifts when loading starts or ends. */}
        <Fade in={isFetchingCalendarData} unmountOnExit>
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: LOADING_INDICATOR_HEIGHT
            }}
          />
        </Fade>
      </div>
      <style>{`
        /* Resource row height */
        .fc-timeline .fc-resource {
          min-height: ${CALENDAR_ITEM_HEIGHT};
          height: ${CALENDAR_ITEM_HEIGHT};
          line-height: ${CALENDAR_ITEM_HEIGHT};
        }

        /* Event height inside the row */
        .fc-timeline-event {
          min-height: ${CALENDAR_ITEM_HEIGHT};
          line-height: ${CALENDAR_ITEM_HEIGHT};
        }
        .fc .fc-scrollgrid-section-header.fc-scrollgrid-section-sticky>* {
            top: ${toolbarBarHeight > 0 ? toolbarBarHeight + 'px' : '3.5rem'};
        }

        /* Story 175 (RN44-RN46): the fabrication order marks, on the BORDER only (the fill is Sun's). A bar
           carries at most one of them: the selection wins over the delay. */
        .fc-timeline-event.${LATE_FO_CLASS} {
          border-color: ${LATE_FO_BORDER_COLOR} !important;
        }
        .fc-timeline-event.${SELECTED_FO_CLASS} {
          border-color: ${SELECTED_FO_BORDER_COLOR} !important;
          border-width: 2px !important;
          z-index: 5 !important;
        }
        /* While an order is selected every other bar steps back, so its jobs stand out on a full Gantt. */
        .${HAS_SELECTED_FO_CLASS} .fc-timeline-event:not(.${SELECTED_FO_CLASS}) {
          opacity: 0.45;
          filter: saturate(0.6);
          transition: opacity 0.2s ease, filter 0.2s ease;
        }
      `}
      </style>

      {/* The panel is a COLUMN, not an overlay: with it open the calendar narrows and every row
          stays readable. That is the whole point of replacing the tooltip, which covered exactly the
          rows the user had opened the detail to compare against. */}
      <div className={panelEvent ? 'csp-split csp-split--open' : 'csp-split'}>
        <div className={hasSelectedFabricationOrder ? 'csp-split__calendar ' + HAS_SELECTED_FO_CLASS : 'csp-split__calendar'}>
          <FullCalendar
            schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_KEY}
            locales={allLocales}
            locale={locale}
            ref={calendarRef}
            headerToolbar={null}
            plugins={useMemo(() => { return [resourceTimelinePlugin, interactionPlugin] }, [])}
            datesSet={useCallback((setDatesInfo) => setCurrentSetDatesInfo(setDatesInfo), [])}
            initialView='resourceTimeLine'
            views={views}
            nowIndicator
            resources={useCallback((info) => trackFetch(() => getResources(info)), [getResources, trackFetch])}
            events={useCallback((info) => trackFetch(async () => {
              if (isPlannerWorking) {
                return []
              }
              const data = await loadPlanification(info.start.valueOf(), info.end.valueOf(), groupby)
              // Set late and borderColor
              data.forEach((event) => updateLate(event))
              return data
            }), [groupby, isPlannerWorking, loadPlanification, updateLate, trackFetch])}
            resourceAreaWidth='25%'
            resourceAreaColumns={resourceAreaColumns}
            eventMaxStack={1}
            eventMinWidth={5}
            editable={groupby === 'devices' && selectedFile && selectedFile.fileName !== 'Current'}
            eventDurationEditable={false}
            eventDrop={eventDrop}
            resourceOrder={groupby !== 'devices' ? 'wait,-firstWait,title,id' : 'wait,-firstWait,orderIndex,title,id'}
            height={groupby === 'parts' ? 500 : 'auto'}
            eventClick={onEventClickCallback}
            eventClassNames={eventClassNames}
            eventOrder={eventOrder}
            rerenderDelay={200}
            resourceLaneDidMount={onSlotLaneDidMount}
            lazyFetching={false}
            moreLinkContent={useCallback((info) => {
              return <Chip label={info.shortText} size='small' />
            }, [])}
            moreLinkDidMount={useCallback((info) => {
              info.el.style.lineHeight = '1rem'
              info.el.style.padding = 0
            }, [])}
            eventDidMount={useCallback((info) => {
              info.el.style.padding = 0
              info.el.style.height = CALENDAR_ITEM_HEIGHT
            }, [])}
            eventContent={useCallback((/** @type {EventInfo} Event info param */ eventInfo) => {
              if (eventInfo.event.extendedProps?.wait) {
                return <CalendarWaitItem event={eventInfo.event} />
              }
              if (!eventInfo.event.extendedProps?.jobEvent) {
                return <CalendarEventItem event={eventInfo.event} />
              }
              let familyResult = { isFamily: false, currentLevel: null, parentLevel: null }
              if (isFamilyViewActive === true) {
                familyResult = isThisEventFamily(eventInfo.event, resourceFamily)
              }
              return (
                <JobEventItem
                  event={eventInfo.event}
                  isFamily={familyResult.isFamily}
                  currentLevel={familyResult.currentLevel}
                  parentLevel={!eventInfo.event.extendedProps.nextJob ? familyResult.parentLevel : null}
                  isFamilyViewActive={isFamilyViewActive}
                  unitCriteriaJob={unitCriteriaName === 'Job'}
                />
              )
            }, [unitCriteriaName, resourceFamily, isThisEventFamily, isFamilyViewActive])}
            slotLabelContent={useCallback((arg) => {
              return arg.level === 0
                ? formatDate(arg.date)
                : arg.view.type === 'resourceTimeLine' ? formatTime(arg.date) : arg.text
            }, [formatDate, formatTime]
            )}
          />
        </div>
        {panelEvent
          ? (
            <CalendarSidePanel
              event={panelEvent}
              unitCriteriaJob={unitCriteriaName === 'Job'}
              onClose={() => setPanelEvent(null)}
              actions={panelEvent.extendedProps?.jobEvent
                ? (
                  <CalendarSidePanelActions onOpen={openEventDialog} />
                  )
                : null}
            />
            )
          : null}
      </div>
    </Stack>

  )
}

export default React.memo(Calendar)
